import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { DevicePerson } from '@kirin/api-client/contracts/device-inventory';
import {
  OrganizationUserStatus,
  type OrganizationMember,
} from '@kirin/api-client/contracts/organization-members';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useMembers } from '@/hooks/api/useMembers';
import { queryKeys } from '@/hooks/api/queryKeys';
import { useToast } from '@/hooks/use-toast';
import { isAccountMember } from '@/lib/member-kinds';
import { cn, getUserDisplayName } from '@/lib/utils';
import { linkDevicePerson } from './api';
import { personLabel } from './deviceDisplay';

/** KIR-5413: who an Unattributed person can be linked to. */
function canBeLinkTarget(member: OrganizationMember): boolean {
  return (
    isAccountMember(member) &&
    Boolean(member.email) &&
    member.authStatus !== OrganizationUserStatus.Disabled
  );
}

/**
 * KIR-5413 — "Link to user" on an Unattributed row of the endpoint page: pick
 * an account member with an email; the person's past and future activity
 * moves to them.
 */
export function LinkPersonDialog({
  organizationId,
  person,
  onClose,
}: {
  organizationId: string;
  /** The Unattributed person to link; the dialog is open while set. */
  person: DevicePerson | null;
  onClose: () => void;
}): ReactElement {
  return (
    <Dialog open={person !== null} onOpenChange={open => !open && onClose()}>
      {person ? (
        <LinkPersonForm
          organizationId={organizationId}
          person={person}
          onClose={onClose}
        />
      ) : null}
    </Dialog>
  );
}

function LinkPersonForm({
  organizationId,
  person,
  onClose,
}: {
  organizationId: string;
  person: DevicePerson;
  onClose: () => void;
}): ReactElement {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: members, isLoading } = useMembers(organizationId);

  const candidates = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (members ?? [])
      .filter(canBeLinkTarget)
      .filter(
        member =>
          !term ||
          (member.name ?? '').toLowerCase().includes(term) ||
          (member.email ?? '').toLowerCase().includes(term)
      );
  }, [members, search]);

  const label = personLabel(person);
  const mutation = useMutation({
    mutationFn: (targetUserId: string) =>
      linkDevicePerson(organizationId, person.userId, targetUserId),
    onSuccess: async () => {
      toast({
        title: 'Linked',
        description: `${label}'s activity now shows under the chosen user.`,
        variant: 'success',
      });
      onClose();
      // Every view that names the person: endpoints, Users roster and tab
      // (userSecurityPosture), and this org's grouped alerts.
      const [orgScope, alertsKind] =
        queryKeys.organizations.groupedAlerts(organizationId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.devices.all }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.organizations.members(organizationId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.userSecurityPosture.all,
        }),
        queryClient.invalidateQueries({
          predicate: query => {
            const [scope, kind, params] = query.queryKey;
            return (
              scope === orgScope &&
              kind === alertsKind &&
              (params as { organizationId?: string } | undefined)
                ?.organizationId === organizationId
            );
          },
        }),
      ]);
    },
  });

  return (
    <DialogContent
      onEscapeKeyDown={event => mutation.isPending && event.preventDefault()}
      onInteractOutside={event => mutation.isPending && event.preventDefault()}
    >
      <DialogHeader>
        <DialogTitle>Link {label} to a user</DialogTitle>
        <DialogDescription>
          Everything Kirin saw from this OS account, on every endpoint, moves to
          the user you pick, and future activity is attributed to them. This
          cannot be undone.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        <Input
          value={search}
          placeholder="Search by name or email"
          aria-label="Search users"
          onChange={event => setSearch(event.target.value)}
        />
        <div
          role="radiogroup"
          aria-label="Users"
          className="max-h-64 space-y-1 overflow-y-auto"
        >
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading users...</p>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No users with an email match.
            </p>
          ) : (
            candidates.map(member => (
              <button
                key={member.id}
                type="button"
                role="radio"
                aria-checked={selected === member.id}
                onClick={() => setSelected(member.id)}
                className={cn(
                  'flex w-full min-w-0 flex-col rounded-md px-2 py-2 text-left hover:bg-muted',
                  selected === member.id && 'bg-muted ring-1 ring-primary'
                )}
              >
                <span className="truncate text-sm">
                  {getUserDisplayName(member)}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {member.email}
                </span>
              </button>
            ))
          )}
        </div>
        {mutation.error ? (
          <p className="text-sm text-destructive" role="alert">
            {mutation.error instanceof Error
              ? mutation.error.message
              : 'Failed to link'}
          </p>
        ) : null}
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => selected && mutation.mutate(selected)}
          disabled={!selected || mutation.isPending}
        >
          {mutation.isPending ? 'Linking...' : 'Link'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
